import type { Route } from '@/services/router/routes';
import type { RegisteredRouter } from '@tanstack/react-router';

type RegisteredRoutePath = keyof RegisteredRouter['routesByPath'];
type RegisteredRoutePathWithSearch = `${RegisteredRoutePath}?${string}`;

/**
 * Human-readable labels for command palette navigation search.
 * Labels match what users see in breadcrumbs and the UI nav.
 * Routes not listed here fall back to auto-generated labels from path segments.
 * Update this file when adding or renaming pages.
 *
 * Typed route keys only. Routes whose keys the typechecker can't resolve live in
 * `getNavigationLabels` below, not here. Prefer `getNavigationLabels()` over reading
 * this directly.
 */
const HYDRA_STATIC_NAVIGATION_LABELS = Object.freeze({
  // ── Home ──────────────────────────────────────────────────────────────────
  '/workspaces/$workspaceId/home': 'Home',
  '/workspaces/$workspaceId/home/add-people': 'Home / Add people',

  // ── Agent ─────────────────────────────────────────────────────────────────
  '/workspaces/$workspaceId/agent': 'Agent',
  '/workspaces/$workspaceId/agent/routines': 'Agent / Routines',
  '/workspaces/$workspaceId/agent/skills': 'Agent / Skills',

  // ── Data ──────────────────────────────────────────────────────────────────
  // The landing URL, which resolves to a browser. A literal `$type` would
  // reach the page only through its unrecognised-segment fallback.
  '/workspaces/$workspaceId/data-index/': 'Data index',
  '/workspaces/$workspaceId/imports/': 'Imports',

  // ── Health ────────────────────────────────────────────────────────────────
  '/workspaces/$workspaceId/health': 'Workspace health',
  '/workspaces/$workspaceId/health?tab=services': 'Workspace health / Services',
  '/workspaces/$workspaceId/health?tab=data': 'Workspace health / Data',
  '/workspaces/$workspaceId/health?tab=messaging': 'Workspace health / Messaging',

  // ── Analysis ─────────────────────────────────────────────────────────────
  // The Deliverability and Email clients entries live in getNavigationLabels below.

  // ── Send messages ─────────────────────────────────────────────────────────
  '/workspaces/$workspaceId/sign-up-forms': 'Sign-up forms',
  '/workspaces/$workspaceId/journeys/message-library': 'Message library',

  // ── Manage audience ───────────────────────────────────────────────────────
  // Goals is not a migrated page — the embedded app still renders it — but its URL is claimed
  // by a TanStack route that gates it on `show-goals` (FLO-4772), so the key belongs in this
  // typed map rather than the child-app one below. `searchNavigation` hides the entry outright
  // when the flag is off (see the ROUTE_GUARDS clause in use-navigation-search.ts).
  '/workspaces/$workspaceId/journeys/goals/': 'Goals',

  // ── Create content ────────────────────────────────────────────────────────
  '/workspaces/$workspaceId/assets': 'Assets',
  '/workspaces/$workspaceId/snippets': 'Snippets',

  // ── Configure data ────────────────────────────────────────────────────────
  '/workspaces/$workspaceId/journeys/exports': 'Exports',
  '/workspaces/$workspaceId/collections': 'Collections',
  '/workspaces/$workspaceId/objects/': 'Custom objects',
  '/workspaces/$workspaceId/objects/$slug/': 'Custom objects',
  // The migrated URL: correct in both flag states, since flag-off it forwards to the `journeys`
  // twin the embedded app serves.
  '/workspaces/$workspaceId/integrations/reporting-webhooks/': 'Integrations / Reporting webhooks',
  // Same reasoning for the three warehouse connector pages (FPS-2698). These replace a single
  // `journeys/integrations/warehouse` entry that pointed at a URL which has never rendered
  // anything: Ember declares that segment with child routes and no index template, so the palette
  // led to a blank page. Each connector is a real page, and keeping "Warehouse" in the label keeps
  // the search term working.
  '/workspaces/$workspaceId/integrations/warehouse/redshift': 'Integrations / Warehouse / Amazon Redshift',
  '/workspaces/$workspaceId/integrations/warehouse/bigquery': 'Integrations / Warehouse / Google BigQuery',
  '/workspaces/$workspaceId/integrations/warehouse/snowflake': 'Integrations / Warehouse / Snowflake',

  // ── Pipelines (CDP) ───────────────────────────────────────────────────────
  '/workspaces/$workspaceId/pipelines/': 'Pipelines',
  '/workspaces/$workspaceId/pipelines/sources/': 'Pipelines / Sources',
  '/workspaces/$workspaceId/pipelines/sources/journeys': 'Pipelines / Sources / Journeys',
  '/workspaces/$workspaceId/pipelines/destinations/': 'Pipelines / Destinations',
  '/workspaces/$workspaceId/pipelines/destinations/journeys': 'Pipelines / Destinations / Journeys',

  // ── Workspace settings (Hydra-owned) ──────────────────────────────────────
  '/workspaces/$workspaceId/settings/': 'Workspace settings',
  '/workspaces/$workspaceId/settings/ai-business-profile': 'Workspace settings / Business context',
  '/workspaces/$workspaceId/settings/audit-logs': 'Workspace settings / Audit logs',
  '/workspaces/$workspaceId/settings/api-keys': 'Workspace settings / API keys',
  '/workspaces/$workspaceId/settings/edit': 'Workspace settings / General',
  '/workspaces/$workspaceId/settings/geofences': 'Workspace settings / Geofences',
  '/workspaces/$workspaceId/settings/api_credentials/': 'Workspace settings / API credentials',
  '/workspaces/$workspaceId/settings/api_credentials/?keyType=tracking':
    'Workspace settings / API credentials / Tracking API keys',
  '/workspaces/$workspaceId/settings/api_credentials/?keyType=app':
    'Workspace settings / API credentials / App API keys',
  '/workspaces/$workspaceId/settings/api_credentials/?keyType=signing':
    'Workspace settings / API credentials / Webhook signing keys',
  '/workspaces/$workspaceId/settings/api_credentials/?keyType=service_accounts':
    'Workspace settings / API credentials / Service accounts',
  '/workspaces/$workspaceId/settings/language': 'Workspace settings / Language',
  '/workspaces/$workspaceId/settings/objects/': 'Workspace settings / Custom object types',
  '/workspaces/$workspaceId/settings/merge_options': 'Workspace settings / Merge options',
  '/workspaces/$workspaceId/settings/llm_configuration/': 'Workspace settings / LLM configuration',
  '/workspaces/$workspaceId/settings/suppressions': 'Workspace settings / Suppressions',
  '/workspaces/$workspaceId/settings/consent': 'Workspace settings / Consent',
  '/workspaces/$workspaceId/settings/webhook_configuration/': 'Workspace settings / Webhooks',
  '/workspaces/$workspaceId/settings/actions/sms-providers': 'Workspace settings / Custom SMS',
  '/workspaces/$workspaceId/settings/actions/sms': 'Workspace settings / SMS',
  '/workspaces/$workspaceId/settings/actions/twilio': 'Workspace settings / Twilio SMS',
  '/workspaces/$workspaceId/settings/actions/url_params/': 'Workspace settings / URL parameters',
  '/workspaces/$workspaceId/settings/actions/time_zone_match': 'Workspace settings / Time zone matching',
  '/workspaces/$workspaceId/settings/actions/slack': 'Workspace settings / Slack',
  '/workspaces/$workspaceId/settings/actions/line': 'Workspace settings / LINE',
  '/workspaces/$workspaceId/settings/actions/apps/': 'Workspace settings / Mobile apps',
  '/workspaces/$workspaceId/settings/actions/push': 'Workspace settings / Push',
  '/workspaces/$workspaceId/settings/actions/in_app': 'Workspace settings / In-App',
  '/workspaces/$workspaceId/settings/actions/whatsapp': 'Workspace settings / WhatsApp',
  '/workspaces/$workspaceId/settings/actions/message_frequency_limits': 'Workspace settings / Message frequency limits',
  // The email tabs are TSR-owned now (FPS-2349); `/settings/actions/email/account_verification`
  // is not part of that migration and stays in the manual map below.
  '/workspaces/$workspaceId/settings/actions/email': 'Workspace settings / Email',
  '/workspaces/$workspaceId/settings/actions/email/sending_domains/': 'Workspace settings / Email / Sending domains',
  '/workspaces/$workspaceId/settings/actions/email/dynamic_addresses': 'Workspace settings / Email / Dynamic addresses',
  '/workspaces/$workspaceId/settings/actions/email/smtp': 'Workspace settings / Email / SMTP',
  '/workspaces/$workspaceId/settings/actions/email/suppression_list': 'Workspace settings / Email / Suppression list',
  '/workspaces/$workspaceId/settings/actions/email/defaults': 'Workspace settings / Email / Defaults',
  // The subscription center subtree is TSR-owned (FPS-2356) and Hydra-serves every URL.
  '/workspaces/$workspaceId/settings/subscription_center': 'Workspace settings / Subscription center',
  '/workspaces/$workspaceId/settings/subscription_center/settings':
    'Workspace settings / Subscription center / Settings',
  '/workspaces/$workspaceId/settings/subscription_center/branding/edit':
    'Workspace settings / Subscription center / Branding',
  '/workspaces/$workspaceId/settings/subscription_center/localization/edit':
    'Workspace settings / Subscription center / Localization',

  // ── Account settings ──────────────────────────────────────────────────────
  '/settings/': 'Account settings',
  '/settings/audit-logs': 'Account settings / Audit logs',
  // Billing is TSR-owned. The retired tab URLs only redirect, so they are not listed.
  '/settings/billing': 'Account settings / Billing',
  '/settings/billing/overview': 'Account settings / Billing / Overview',
  '/settings/billing/usage': 'Account settings / Billing / Usage',
  '/settings/billing/billing-history': 'Account settings / Billing / Billing history',
  '/settings/billing/available-plans/journeys': 'Account settings / Billing / Available plans',
  '/settings/billing/billing-details': 'Account settings / Billing / Billing details',
  '/settings/billing/checkout': 'Account settings / Billing / Checkout',
  '/settings/receipts': 'Account settings / Receipts',
  '/settings/subscription': 'Account settings / Subscription',
  '/settings/account/': 'Account settings / Account',
  '/settings/roles/': 'Account settings / Roles',
  '/settings/security/': 'Account settings / Security',
  '/settings/security/sso': 'Account settings / Security / SSO',
  '/settings/privacy': 'Account settings / Privacy',
  '/settings/ai': 'Account settings / AI & MCP',
  '/settings/workspaces/': 'Account settings / Workspaces',
  '/settings/api_credentials/': 'Account settings / API credentials',
  '/settings/api-keys': 'Account settings / API keys',
  '/settings/experimental-features': 'Account settings / Experimental features',
  // The personal-settings index (FPS-2861) and the 2FA screens (FPS-2292) are TSR-owned.
  // Only the index and the security landing page are listed: the enrollment steps are
  // reachable only by working through the flow, so they would be dead ends in the palette.
  '/settings/personal/': 'Personal settings',
  '/settings/personal/security/': 'Personal settings / Security',
} satisfies Partial<Record<RegisteredRoutePath | RegisteredRoutePathWithSearch, string>>);

const CHILD_APP_STATIC_NAVIGATION_LABELS = Object.freeze({
  // ── Design Studio ─────────────────────────────────────────────────────────
  // `/design-studio/dashboard` (CON-7495) and `/design-studio/global-styles`
  // (CON-7588) are TanStack routes now, so their entries live in
  // getNavigationLabels below, not this typed child-app map (a TSR route key
  // can't type this manual map).

  // ── Send messages ─────────────────────────────────────────────────────────
  // Note: the Automations and Broadcasts entries live in getNavigationLabels below.
  '/workspaces/:workspaceId/journeys/transactional': 'Transactional',
  '/workspaces/:workspaceId/journeys/anonymous': 'Anonymous',

  // ── Create content ────────────────────────────────────────────────────────
  // `/layouts` (list) and `/layouts/starters` are Hydra/TanStack
  // routes now, so both live in getNavigationLabels below, not this typed
  // child-app map (a TSR route key can't type this manual map).

  // ── Manage audience ───────────────────────────────────────────────────────
  // Note: the People entry lives in getNavigationLabels below.
  '/workspaces/:workspaceId/journeys/segments': 'Segments',
  '/workspaces/:workspaceId/journeys/segments/explore': 'Segment explorer',
  // `journeys/goals` moved to HYDRA_STATIC_NAVIGATION_LABELS above: the index is a TanStack
  // route now (FLO-4772), so its key types that map and not this manual child-app one.

  // ── Review ────────────────────────────────────────────────────────────────
  // Note: the Reports, Profile activity, and Message activity entries live in
  // getNavigationLabels below.

  // ── Configure data ────────────────────────────────────────────────────────
  // `journeys/integrations/warehouse` used to sit here. It renders nothing — see the three
  // connector entries in the Hydra map above, which replaced it. `journeys/imports` moved to the
  // Hydra map with FPS-2657, and `journeys/data-index` with FPS-2310.

  // ── Pipelines (CDP) ───────────────────────────────────────────────────────
  '/workspaces/:workspaceId/pipelines/sources/reverse-etl': 'Pipelines / Sources / Reverse ETL',
  '/workspaces/:workspaceId/pipelines/connections': 'Pipelines / Connections',
  '/workspaces/:workspaceId/pipelines/catalog': 'Pipelines / Catalog',

  // ── Workspace settings / channel actions ──────────────────────────────────
  '/workspaces/:workspaceId/settings/actions/email/account_verification':
    'Workspace settings / Email / Account verification',

  // ── Account settings ──────────────────────────────────────────────────────
  // `/settings/security` and its `/sso` page are TSR-owned now (FPS-2223 / FPS-2737); their labels
  // live in HYDRA_STATIC_NAVIGATION_LABELS above, as do `/settings/personal` (FPS-2861) and its
  // `/security` sub-path (FPS-2292).
} satisfies Partial<Record<Route, string>>);

const STATIC_NAVIGATION_LABELS = Object.freeze({
  ...HYDRA_STATIC_NAVIGATION_LABELS,
  ...CHILD_APP_STATIC_NAVIGATION_LABELS,
});

// Change only this destination when the Websites settings page lands.
export const WEB_PUSH_SETTINGS_ROUTE = '/workspaces/$workspaceId/settings/actions/push/web';

export const NAVIGATION_KEYWORDS = new Map<string, string[]>([
  [WEB_PUSH_SETTINGS_ROUTE, ['web push', 'browser push', 'web notifications', 'VAPID']],
]);

/**
 * Full route→label map for command palette navigation. This is the single source of
 * truth for navigable route definitions.
 */
export function getNavigationLabels(): Record<string, string> {
  return {
    ...STATIC_NAVIGATION_LABELS,
    [WEB_PUSH_SETTINGS_ROUTE]: 'Workspace settings / Push / Web',

    // Migrated Hydra routes served at their canonical URLs. Kept here (untyped
    // map) rather than STATIC_NAVIGATION_LABELS because their typed route keys
    // don't reliably resolve as `KnownRoute` under CI's typechecker.
    '/workspaces/:workspaceId/layouts': 'Email layouts',
    '/workspaces/:workspaceId/layouts/starters': 'Email layouts / starters',
    '/workspaces/:workspaceId/design-studio/dashboard': 'Design Studio',
    '/workspaces/:workspaceId/design-studio/global-styles': 'Design Studio / Global Styles',
    '/workspaces/:workspaceId/journeys/integrations': 'Integrations',

    // ── Review ────────────────────────────────────────────────────────────────
    '/workspaces/:workspaceId/reports': 'Reports',
    '/workspaces/:workspaceId/reports/deliverability': 'Deliverability analysis',
    '/workspaces/:workspaceId/reports/email-clients': 'Email clients analysis',
    '/workspaces/:workspaceId/profile_activity/identified': 'Profile activity',
    '/workspaces/:workspaceId/message_activity': 'Message activity',
    '/workspaces/:workspaceId/message_activity/sent': 'Sent',
    '/workspaces/:workspaceId/message_activity/drafts': 'Drafts',

    // ── Manage audience ───────────────────────────────────────────────────────
    '/workspaces/:workspaceId/people': 'People',

    // ── Send messages ─────────────────────────────────────────────────────────
    '/workspaces/:workspaceId/journeys/automations': 'Automations',
    '/workspaces/:workspaceId/journeys/broadcasts': 'Broadcasts',
    '/workspaces/:workspaceId/journeys/broadcasts/one_time_sends': 'Broadcasts / One-time sends',
    '/workspaces/:workspaceId/journeys/broadcasts/api-triggered': 'Broadcasts / API-triggered',
  };
}
